import { useCallback, useId, useRef, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { FolderItemContextMenu } from "@/components/doc/left-sidebar/documents/FolderItemContextMenu";
import { useCreateHintHoverHandlers } from "@/components/hints/useHintHover";
import { Icon } from "@/components/icons/Icon";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { useStore } from "@/store";
import { isDocument } from "@/store/DocumentTreeStore";
import { twMerge } from "@/utils/twMerge";

import { SectionDivider } from "../SectionDivider";
import { UnreadDot } from "../UnreadDot";
import { useRowFocusWithin } from "../useRowFocusWithin";
import { TreeItem } from "../useSidebarNav";

import { Add } from "./Add";
import { DocItem } from "./DocumentItem";
import { Draggable } from "./Draggable";
import { Droppable } from "./Droppable";
import { ListItemTitle } from "./ListItemTitle";

import type { Folder } from "@/api";
import type { IconName } from "@/components/icons/Icon";
import type { DocumentTreeStore } from "@/store/DocumentTreeStore";
import type { HintKey } from "@/store/HintsStore";

export type RenderContext = "public-profile" | "document-tree" | "recent" | "search";

export const FolderItems = observer(function FolderItems({
  folder,
  className,
  isReadOnly,
  childrenDroppable = true,
  sortable = true,
  emptyFolder,
  bottomComponent,
  isInTrash,
  invertedColor = true,
  renderContext,
  documentTree,
  isMobile,
  visibleFilesCount,
  filesContainerRef,
  listId,
}: {
  folder: Folder;
  className?: string;
  isReadOnly?: boolean | "deep";
  childrenDroppable?: boolean;
  sortable?: boolean;
  emptyFolder?: React.ReactNode;
  bottomComponent?: React.ReactNode;
  isInTrash?: boolean;
  invertedColor?: boolean;
  renderContext?: RenderContext;
  documentTree: DocumentTreeStore;
  isMobile?: boolean;
  visibleFilesCount?: number;
  filesContainerRef?: React.Ref<HTMLUListElement>;
  listId?: string;
}) {
  const t = useTranslations();

  const hasPlaceholdersInFolder = documentTree.fileUploadPlaceholders.some(
    (placeholder) => placeholder.parentId === folder.uid,
  );
  const hasFileUploadPlaceholder = (position: number) => {
    return documentTree.fileUploadPlaceholders.some(
      (placeholder) => placeholder.position === position && placeholder.parentId === folder.uid,
    );
  };

  const uploadFilePlaceholder = (
    <div className="h-8 mb-1 px-5 text-size-15 leading-[18px] rounded-6 bg-white dark:bg-black flex items-center justify-between">
      <span>{t("documentTree.fileUpload.uploading")}</span>
      <Icon name="Spinner" className="animate-spin w-6 h-6 [--icon-main:var(--icon-active)]" />
    </div>
  );

  const visibleFiles =
    visibleFilesCount === undefined ? folder.files : folder.files?.slice(0, visibleFilesCount);

  return (
    <div className={twMerge("relative", className)}>
      {!folder.files?.length && !hasPlaceholdersInFolder && emptyFolder}
      <ul id={listId} ref={filesContainerRef} role="group">
        {visibleFiles?.map((item, index) => (
          <li key={item.uid} role="none" className="relative">
            {hasFileUploadPlaceholder(index + 1) && uploadFilePlaceholder}
            {isDocument(item) ? (
              <DocItem
                docItem={item}
                position={index + 1}
                parentId={folder.uid}
                sortable={sortable}
                isInTrash={isInTrash}
                invertedColor={invertedColor && !isInTrash}
                renderContext={renderContext}
                isMobile={isMobile}
              />
            ) : (
              // eslint-disable-next-line no-use-before-define
              <FolderItem
                folder={item}
                position={index + 1}
                parentId={folder.uid}
                isReadOnly={isReadOnly === "deep" && !!isReadOnly}
                droppable={childrenDroppable}
                childrenDroppable={childrenDroppable}
                sortable={sortable}
                invertedColor={invertedColor && !isInTrash}
                emptyFolder={renderContext === "document-tree" && <Add.Document folder={item} />}
                renderContext={renderContext}
                isMobile={isMobile}
              />
            )}
          </li>
        ))}
      </ul>
      {folder.files && hasFileUploadPlaceholder(folder.files.length + 1) && uploadFilePlaceholder}
      <Droppable
        id={`${folder.uid}-after`}
        type="folder"
        position={(folder.files?.length || 0) + 1}
        parentId={folder.uid}
        disabled={!childrenDroppable}
        className="h-[20px] translate-y-[10px]"
        compensateHeight
      />
      {bottomComponent}
    </div>
  );
});

const UnreadMessagesIndicator = observer(function UnreadMessagesIndicator({
  folder,
  className,
}: {
  folder: Folder;
  hasRightButton?: boolean;
  className?: string;
}) {
  function hasUnreadMessages(f: Folder): boolean {
    return (
      f.files?.some((file) =>
        isDocument(file) ? file.unreadMessagesCount > 0 : hasUnreadMessages(file),
      ) ?? false
    );
  }

  if (!hasUnreadMessages(folder)) {
    return null;
  }

  return <UnreadDot className={className} />;
});

type FolderInnerProps = {
  folder: Folder;
  isOverlay?: boolean;
  draggable: boolean;
  position?: number;
  parentId?: string;
  isReadOnly?: boolean | "deep";
  isEditing: boolean;
  isOpen: boolean;
  onFolderClick: (e: React.MouseEvent) => void;
  onUpdate: (newTitle: string) => void;
  showTooltip: boolean;
  tooltip: React.ReactNode;
  handlerProps: Record<string, unknown>;
  withExpandIcon: boolean;
  iconName?: IconName;
  title?: string;
  titleClassName: string;
  rightButton?: React.ReactNode;
  className?: string;
  invertedColor: boolean;
  childrenDroppable: boolean;
  sortable: boolean;
  emptyFolder?: React.ReactNode;
  bottomComponent?: React.ReactNode;
  isTrash?: boolean;
  renderContext: RenderContext;
  isMobile?: boolean;
  hintKey?: HintKey;
  withDividerWhenOpen?: boolean;
  visibleFilesCount?: number;
  filesContainerRef?: React.Ref<HTMLUListElement>;
};

const FolderInner = observer(function FolderInner({
  folder,
  isOverlay,
  draggable,
  position,
  parentId,
  isReadOnly,
  isEditing,
  isOpen,
  onFolderClick,
  onUpdate,
  showTooltip,
  tooltip,
  handlerProps,
  withExpandIcon,
  iconName,
  title,
  titleClassName,
  rightButton,
  className,
  invertedColor,
  childrenDroppable,
  sortable,
  emptyFolder,
  bottomComponent,
  isTrash,
  renderContext,
  isMobile,
  hintKey,
  withDividerWhenOpen,
  visibleFilesCount,
  filesContainerRef,
}: FolderInnerProps) {
  const [hover, setHover] = useState(false);
  const treeItemRef = useRef<HTMLDivElement>(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const { documentTree } = useStore();
  const t = useTranslations();
  const createHintHandlers = useCreateHintHoverHandlers();
  const listId = useId();
  const { focusWithin, focusWithinProps } = useRowFocusWithin();
  // The header's button is a Tab stop only while the folder holds focus
  const controlTabIndex = focusWithin ? undefined : -1;
  const isRootFolder = documentTree.isRootFolder(folder);
  const label = title || folder.title || "";

  const isDuplicating = folder.isDuplicating ?? false;

  if (isDuplicating) {
    return (
      <div
        className={twMerge(
          "px-2.5 py-3.5 rounded-8 bg-white dark:bg-black flex flex-col",
          "text-[15px] leading-[18px]",
        )}
      >
        <span className="font-semibold">{t("documentTree.duplicatingFolder.title")}:</span>
        <span className="font-semibold">{folder.title}</span>
        <div className="w-full h-[2px] mt-2.5 bg-gray-200 dark:bg-gray-800 overflow-hidden">
          <div className="h-full w-2/5 bg-blue-500 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <TreeItem
      ref={treeItemRef}
      focused={focusWithin}
      {...focusWithinProps}
      aria-label={label}
      isParent
      isOpen={isOpen}
      onToggle={() => documentTree.toggleOpenFolder(folder)}
      aria-expanded={isOpen}
      className={twMerge(
        documentTree.dndHighlightFolderMap.get(folder) &&
          !isRootFolder &&
          "bg-sidebar-button-hover-inverted-bg rounded-6",
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Draggable
        id={folder.uid}
        type="folder"
        isOverlay={isOverlay}
        position={position}
        parentId={parentId}
        disabled={!draggable}
      >
        <div
          className={twMerge(
            "relative flex group rounded-6 select-none",
            !isOverlay && invertedColor
              ? "hover:bg-sidebar-button-hover-inverted-bg [&:has(button[data-open])]:bg-sidebar-button-hover-inverted-bg group-hover:bg-transparent sm:hover:bg-transparent [&:has(.right-button:hover)]:bg-transparent"
              : "hover:bg-sidebar-button-hover-bg [&:has(button[data-open])]:bg-sidebar-button-hover-bg group-hover:bg-transparent sm:hover:bg-transparent [&:has(.right-button:hover)]:bg-transparent",
            isEditing && "bg-sidebar-button-hover-inverted-bg ring-1 ring-blue-500",
            className,
          )}
        >
          <button
            {...handlerProps}
            type="button"
            onClick={onFolderClick}
            tabIndex={controlTabIndex}
            className={twMerge(
              "flex items-center w-full min-h-7.5 cursor-pointer",
              isEditing ? "pr-2" : "pr-7 sm:pr-10",
            )}
            aria-label={label}
            aria-expanded={withExpandIcon ? isOpen : undefined}
            aria-controls={withExpandIcon && isOpen ? listId : undefined}
            {...(hintKey ? createHintHandlers(hintKey) : {})}
          >
            {withExpandIcon && (
              <div className="min-w-4 mx-[3px]">
                <Icon
                  name="ArrowDownLarge"
                  width={16}
                  height={16}
                  className={twMerge("m-auto", !isOpen && "-rotate-90")}
                />
              </div>
            )}
            {iconName && (
              <Icon
                name={iconName}
                width={24}
                height={24}
                className={twMerge(!withExpandIcon && "ml-1", "mr-1")}
              />
            )}
            <ListItemTitle
              type="folder"
              title={title || folder.title || ""}
              onUpdate={onUpdate}
              onStopEdit={() => documentTree.editFolderMap.reset()}
              isEditing={isEditing}
              className={titleClassName}
            />
          </button>
          {(hover || focusWithin || isMobile || contextMenuOpen || isOpen) &&
            !isEditing &&
            !isOverlay &&
            !isReadOnly && (
              <>
                <FolderItemContextMenu
                  documentTree={documentTree}
                  folder={folder}
                  rowRef={treeItemRef}
                  onOpenChange={setContextMenuOpen}
                  renderContext={renderContext}
                />
                {showTooltip && tooltip}
              </>
            )}
          {!isEditing && (
            <UnreadMessagesIndicator
              folder={folder}
              className={twMerge(
                rightButton
                  ? "relative my-auto mr-2 -ml-4"
                  : "absolute top-1/2 -translate-y-1/2 right-1.5 group-has-[button[data-open]]:right-8",
                renderContext === "document-tree" &&
                  !isReadOnly &&
                  "group-hover:right-8 group-has-[:focus-visible]:right-8 sm:!right-12",
              )}
            />
          )}
          {rightButton}
        </div>
      </Draggable>
      {isOpen && withDividerWhenOpen && <SectionDivider className="my-1" />}
      {isOpen && (
        <FolderItems
          folder={folder}
          className={twMerge(!isRootFolder && "ml-[10px]")}
          isReadOnly={isReadOnly}
          childrenDroppable={childrenDroppable}
          sortable={sortable}
          emptyFolder={emptyFolder}
          bottomComponent={bottomComponent}
          isInTrash={isTrash}
          invertedColor={invertedColor}
          renderContext={renderContext}
          isMobile={isMobile}
          documentTree={documentTree}
          visibleFilesCount={visibleFilesCount}
          filesContainerRef={filesContainerRef}
          listId={listId}
        />
      )}
    </TreeItem>
  );
});

export const FolderItem = observer(function FolderItem({
  folder,
  iconName,
  title,
  isReadOnly,
  className,
  isOverlay,
  draggable = true,
  droppable = true,
  childrenDroppable = true,
  sortable = true,
  position,
  parentId,
  emptyFolder,
  bottomComponent,
  isTrash,
  titleClassName = "",
  invertedColor = true,
  withExpandIcon = true,
  rightButton,
  renderContext = "document-tree",
  isMobile,
  hintKey,
  withDividerWhenOpen,
  visibleFilesCount,
  filesContainerRef,
}: {
  folder: Folder;
  iconName?: IconName;
  title?: string;
  // TODO: make it rely on the userPermissions value from BE
  isReadOnly?: boolean | "deep";
  /** This item is draggable, you can take it and move somewhere else */
  draggable?: boolean;
  /** This item is droppable, you can drop something inside this item */
  droppable?: boolean;
  /** You can drop item into the child folder. Nested items inherit this property. */
  childrenDroppable?: boolean;
  /**
   * You can reorder this item, i.e. you can put something before this item.
   * Nested items inherit this property.
   * If the item is draggable, but not sortable, you can move it somewhere else to the sortable folder,
   * but you can't sort it in the current folder.
   */
  sortable?: boolean;
  className?: string;
  /**
   * Currently draggable item is shown on top of other items.
   */
  isOverlay?: boolean;
  position?: number;
  parentId?: string;
  /** Show this if the folder is open and empty */
  emptyFolder?: React.ReactNode;
  /** Show this at the bottom of the folder */
  bottomComponent?: React.ReactNode;
  isTrash?: boolean;
  invertedColor?: boolean;
  withExpandIcon?: boolean;
  titleClassName?: string;
  rightButton?: React.ReactNode;
  renderContext?: RenderContext;
  isMobile?: boolean;
  hintKey?: HintKey;
  /** Show a divider between the folder title and its contents when the folder is open */
  withDividerWhenOpen?: boolean;
  visibleFilesCount?: number;
  filesContainerRef?: React.Ref<HTMLUListElement>;
}) {
  const { documentTree } = useStore();
  const t = useTranslations();

  const isEditing = documentTree.editFolderMap.get(folder);
  const isOpen = documentTree.isOpenFolder(folder);
  const { handlerProps, tooltip } = useTooltip({
    title: folder.title || t("doc.documentTitle.placeholder"),
    placement: "right",
  });
  const showTooltip = !isEditing && !isOverlay;

  const handleFolderClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      documentTree.toggleOpenFolder(folder);
      // For now we don't have specific folder page
      // router.push(folderPageUrl({ folderId: folder.id }));
    },
    [documentTree, folder],
  );

  const handleUpdate = useCallback(
    (newTitle: string) => {
      documentTree.updateFolderTitle(folder, newTitle.trim());
    },
    [documentTree, folder],
  );

  const inner = (
    <FolderInner
      folder={folder}
      isOverlay={isOverlay}
      draggable={draggable}
      position={position}
      parentId={parentId}
      isReadOnly={isReadOnly}
      isEditing={isEditing}
      isOpen={isOpen}
      onFolderClick={handleFolderClick}
      onUpdate={handleUpdate}
      showTooltip={showTooltip}
      tooltip={tooltip}
      handlerProps={handlerProps}
      withExpandIcon={withExpandIcon}
      iconName={iconName}
      title={title}
      titleClassName={titleClassName}
      rightButton={rightButton}
      className={className}
      invertedColor={invertedColor}
      childrenDroppable={childrenDroppable}
      sortable={sortable}
      emptyFolder={emptyFolder}
      bottomComponent={bottomComponent}
      isTrash={isTrash}
      renderContext={renderContext}
      isMobile={isMobile}
      hintKey={hintKey}
      withDividerWhenOpen={withDividerWhenOpen}
      visibleFilesCount={visibleFilesCount}
      filesContainerRef={filesContainerRef}
    />
  );

  return (
    <>
      <Droppable
        id={folder.uid}
        type="folder"
        isOverlay={isOverlay}
        position={position}
        parentId={parentId}
        disabled={!sortable || !draggable}
        className="absolute h-4 left-0 right-0"
      />
      {/* Drop inside the closed folder. Always wraps so toggling doesn't remount the header. */}
      <Droppable
        id={`${folder.uid}-closed`}
        type="folder"
        isOverlay={isOverlay}
        position={0}
        parentId={folder.uid}
        variant="border"
        disabled={
          !droppable ||
          (folder.isDuplicating ?? false) ||
          (isOpen && childrenDroppable && !!folder.files?.length)
        }
      >
        {inner}
      </Droppable>
    </>
  );
});
